import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Card, DataTable, EmptyState, FormCheckbox, FormInput, Modal, StatusBadge } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { formatWallClock, type IdeaLabStudySummary } from './ideaLabUtils';

/**
 * Idea Lab bench: studies group entry-logic variants that are compared under shared evaluation
 * settings. Nothing here creates a strategy or a Trade Logic library entry; variants live only in
 * the Idea Lab tables until one is promoted.
 */
const IdeaLab = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [includeArchived, setIncludeArchived] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const studiesQuery = useAppQuery(
        {
            ...trpc.ideaLab.listStudies.queryOptions({ includeArchived, limit: 200 }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        { fallbackErrorMessage: 'Failed to load Idea Lab studies' }
    );

    const createStudyMutation = useMutation({
        ...trpc.ideaLab.createStudy.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (study) => {
            showToast(`Study "${study.name}" created`, 'success');
            setIsCreateOpen(false);
            setName('');
            setDescription('');
            await queryClient.invalidateQueries(trpc.ideaLab.listStudies.pathFilter());
            void navigate(`/user/ideaLab/study/${study.id}`);
        }
    });

    const studies = useMemo(() => studiesQuery.data ?? [], [studiesQuery.data]);

    const columns: ColumnDef<IdeaLabStudySummary>[] = [
        {
            accessorKey: 'name',
            header: 'Study',
            cell: ({ row }) => (
                <div className="min-w-48">
                    <Link className="font-medium text-blue-300 hover:text-blue-200" to={`/user/ideaLab/study/${row.original.id}`}>
                        {row.original.name}
                    </Link>
                    <div className="text-xs text-zinc-500">#{row.original.id}</div>
                </div>
            )
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: ({ row }) =>
                row.original.description ? (
                    <p className="line-clamp-2 min-w-80 max-w-2xl text-zinc-400" title={row.original.description}>
                        {row.original.description}
                    </p>
                ) : (
                    <span className="text-zinc-600">—</span>
                )
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.status} tone={row.original.status === 'open' ? 'success' : 'neutral'} />
        },
        {
            accessorKey: 'createdAt',
            header: 'Created',
            cell: ({ row }) => <span className="whitespace-nowrap text-zinc-400">{formatWallClock(row.original.createdAt)}</span>
        }
    ];

    const canCreate = name.trim().length > 0 && !createStudyMutation.isPending;

    return (
        <UserLayout containerSize="1400px" title="Idea Lab - Project 314">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-zinc-100">Idea Lab</h1>
                    <p className="mt-1 max-w-3xl text-sm text-zinc-400">
                        Try entry-logic ideas and their subtle variations side by side. A study groups variants; a run freezes the universe,
                        window, and horizons and reports fixed-horizon edge per variant. Nothing becomes a strategy until you promote it.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <FormCheckbox
                        checked={includeArchived}
                        label="Show archived"
                        onChange={(event) => setIncludeArchived(event.target.checked)}
                    />
                    <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                        New study
                    </Button>
                </div>
            </div>

            <Card>
                {studiesQuery.isLoading ? (
                    <DataLoadingSpinner text="Loading studies..." />
                ) : studies.length === 0 ? (
                    <EmptyState
                        action={
                            <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                                Create your first study
                            </Button>
                        }
                        description="A study is a workspace for one idea and its variations."
                        title="No Idea Lab studies yet"
                    />
                ) : (
                    <DataTable columns={columns} data={studies} pagination={{ enabled: true, pageSize: 25 }} search sort />
                )}
            </Card>

            <Modal
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!canCreate}
                            isLoading={createStudyMutation.isPending}
                            variant="primary"
                            onClick={() =>
                                createStudyMutation.mutate({
                                    name: name.trim(),
                                    description: description.trim() === '' ? null : description.trim()
                                })
                            }
                        >
                            Create study
                        </Button>
                    </div>
                }
                isOpen={isCreateOpen}
                size="md"
                title="New Idea Lab study"
                onClose={() => setIsCreateOpen(false)}
            >
                <FormInput
                    label="Name"
                    maxLength={255}
                    placeholder="e.g. RSI dip entries, 5m"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                <FormInput
                    helpText="Optional. What question does this study ask?"
                    label="Description"
                    maxLength={4000}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />
            </Modal>
        </UserLayout>
    );
};

export default IdeaLab;
